/**
 * browserNotify — the single place the ATS talks to the Web Notifications API.
 * (notify-center, 7 Sep 2026)
 *
 * Ruhshad: "everyone should get a browser notification for every single
 * notification received".
 *
 * Rules baked in here, so no caller has to remember them:
 *   1. NEVER prompt on page load.  `requestPermission()` is only ever called
 *      from `askPermission()`, which the panel wires to a real click.
 *   2. One notification per event.  `hasFired(id)` is checked against a
 *      per-user localStorage set AND the notification `tag` is the event id,
 *      so the OS collapses a repeat even across tabs.
 *   3. A refresh never replays history: the high-water mark (newest
 *      created_at we have already announced) is persisted per user.
 *   4. Nothing fires while the tab is visible AND focused, unless the user
 *      ticked "always alert".
 *   5. Clicking focuses the tab and hands the deep link back to the caller.
 *
 * All storage is wrapped — private mode throws on localStorage in Safari.
 */

const NS = 'dcs.notify';
const MAX_REMEMBERED_IDS = 400;

/* ------------------------------------------------------------------ */
/* storage helpers                                                     */
/* ------------------------------------------------------------------ */

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
};

/** Everything is keyed per user id, so two people on one machine never mix. */
export const keyFor = (userId, suffix) => `${NS}.${userId || 'anon'}.${suffix}`;

/* ------------------------------------------------------------------ */
/* preferences (mirrored from the server, cached locally)              */
/* ------------------------------------------------------------------ */

export const DEFAULT_PREFS = {
  browser_enabled: true,
  always_alert: false,
  sound: true,
  prompt_dismissed: false,
  types: {
    mail: true,
    candidate: true,
    interview: true,
    task: true,
    bgv: true,
    agreement: true,
    money: true,
    system: true,
    activity: false,
  },
};

export const FAMILY_LABELS = [
  ['mail', 'New mail in a mailbox I can see'],
  ['candidate', 'Candidates — parsed, shared, HR action'],
  ['interview', 'Interviews scheduled or rescheduled'],
  ['task', 'Tasks and follow-ups assigned to me'],
  ['bgv', 'Background verification updates'],
  ['agreement', 'Agreements and proposals'],
  ['money', 'Placements, invoices and money alerts'],
  ['system', 'Everything else the ATS raises'],
  ['activity', 'Team activity stream (managers, noisy)'],
];

export const loadPrefs = (userId) => {
  const stored = read(keyFor(userId, 'prefs'), null);
  if (!stored || typeof stored !== 'object') return { ...DEFAULT_PREFS, types: { ...DEFAULT_PREFS.types } };
  return {
    ...DEFAULT_PREFS,
    ...stored,
    types: { ...DEFAULT_PREFS.types, ...(stored.types || {}) },
  };
};

export const savePrefs = (userId, prefs) => write(keyFor(userId, 'prefs'), prefs);

/* ------------------------------------------------------------------ */
/* permission                                                          */
/* ------------------------------------------------------------------ */

export const supported = () =>
  typeof window !== 'undefined' && typeof window.Notification !== 'undefined';

/** 'unsupported' | 'default' | 'granted' | 'denied' */
export const permission = () => (supported() ? window.Notification.permission : 'unsupported');

/**
 * Ask the browser.  MUST be called from inside a user gesture handler —
 * Chrome and Safari both drop the request otherwise, and Chrome counts a
 * gesture-less call against the origin.
 */
export async function askPermission() {
  if (!supported()) return 'unsupported';
  if (window.Notification.permission !== 'default') return window.Notification.permission;
  try {
    const result = await window.Notification.requestPermission();
    return result;
  } catch (e) {
    // Older Safari only has the callback form.
    return new Promise((resolve) => {
      try {
        window.Notification.requestPermission(resolve);
      } catch (err) {
        resolve('denied');
      }
    });
  }
}

/** True when we should show the one-line "Turn on desktop alerts" strip. */
export const shouldOfferPrompt = (userId, prefs) =>
  supported() && permission() === 'default' && !(prefs || loadPrefs(userId)).prompt_dismissed;

/* ------------------------------------------------------------------ */
/* de-duplication + high-water mark                                    */
/* ------------------------------------------------------------------ */

export const firedIds = (userId) => {
  const list = read(keyFor(userId, 'fired'), []);
  return Array.isArray(list) ? list : [];
};

export const hasFired = (userId, id) => firedIds(userId).indexOf(id) !== -1;

export function rememberFired(userId, ids) {
  const list = firedIds(userId);
  const merged = list.concat(ids.filter((id) => list.indexOf(id) === -1));
  write(keyFor(userId, 'fired'), merged.slice(-MAX_REMEMBERED_IDS));
}

/** Newest created_at already announced, so a refresh starts after it. */
export const highWater = (userId) => read(keyFor(userId, 'highwater'), null);

export function setHighWater(userId, stamp) {
  if (!stamp) return;
  const current = highWater(userId);
  if (!current || String(stamp) > String(current)) write(keyFor(userId, 'highwater'), String(stamp));
}

export function resetHighWater(userId) {
  write(keyFor(userId, 'highwater'), null);
  write(keyFor(userId, 'fired'), []);
}

/* ------------------------------------------------------------------ */
/* the decision + the fire                                             */
/* ------------------------------------------------------------------ */

export const tabIsActive = () => {
  if (typeof document === 'undefined') return false;
  const visible = document.visibilityState !== 'hidden';
  const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  return visible && focused;
};

/**
 * Should this one item become an OS notification?
 * Returns a reason string when it should not — handy in tests and logs.
 */
export function decide(item, prefs, { userId, active = tabIsActive() } = {}) {
  if (!item || !item.id) return { fire: false, reason: 'no-id' };
  if (!supported()) return { fire: false, reason: 'unsupported' };
  if (permission() !== 'granted') return { fire: false, reason: 'no-permission' };
  const p = prefs || loadPrefs(userId);
  if (!p.browser_enabled) return { fire: false, reason: 'master-off' };
  const family = item.family || 'system';
  if (p.types && p.types[family] === false) return { fire: false, reason: `type-off:${family}` };
  if (active && !p.always_alert) return { fire: false, reason: 'tab-active' };
  const hw = highWater(userId);
  if (hw && item.created_at && String(item.created_at) <= String(hw)) {
    return { fire: false, reason: 'already-seen' };
  }
  if (hasFired(userId, item.id)) return { fire: false, reason: 'duplicate' };
  return { fire: true, reason: 'ok' };
}

/**
 * Fire one browser notification.  `onOpen(item)` is called when the user
 * clicks it, after the tab has been focused.
 */
export function fire(item, { onOpen, icon = '/favicon.ico' } = {}) {
  try {
    const n = new window.Notification(item.title || 'DCS ATS', {
      body: item.body || '',
      // The event id as the tag: the OS replaces rather than stacks, so even
      // two tabs racing produce exactly one visible notification.
      tag: String(item.id),
      renotify: false,
      icon,
      data: { link: item.link, id: item.id },
      silent: false,
    });
    n.onclick = (event) => {
      try {
        if (event && event.preventDefault) event.preventDefault();
        window.focus();
        if (typeof onOpen === 'function') onOpen(item);
      } finally {
        try { n.close(); } catch (e) { /* already gone */ }
      }
    };
    return n;
  } catch (e) {
    // Android Chrome throws here (it requires a service worker); the in-app
    // toast and the bell still work, which is the whole point of not relying
    // on this path.
    return null;
  }
}

/**
 * Announce a batch.  Returns the ids that actually fired.
 * Never throws — a notification failure must never break a poll.
 */
export function announce(items, { userId, prefs, onOpen, active } = {}) {
  const p = prefs || loadPrefs(userId);
  const fired = [];
  let newest = null;
  (items || []).forEach((item) => {
    if (item && item.created_at && (!newest || String(item.created_at) > String(newest))) {
      newest = item.created_at;
    }
    const verdict = decide(item, p, { userId, active });
    if (!verdict.fire) return;
    if (fire(item, { onOpen })) fired.push(item.id);
  });
  if (fired.length) rememberFired(userId, fired);
  // The high-water mark moves for everything we saw, fired or not — a row we
  // deliberately skipped (tab focused, type off) must not come back later.
  if (newest) setHighWater(userId, newest);
  return fired;
}

export default {
  announce,
  askPermission,
  decide,
  fire,
  highWater,
  loadPrefs,
  permission,
  resetHighWater,
  savePrefs,
  setHighWater,
  shouldOfferPrompt,
  supported,
  tabIsActive,
};
